// Secure token storage utility
import CryptoJS from "crypto-js";

const STORAGE_KEY = "app_session";
const ENCRYPTION_KEY =
  process.env.REACT_APP_ENCRYPTION_KEY ||
  (() => {
    console.warn("⚠️ Using default encryption key. Set REACT_APP_ENCRYPTION_KEY in production!");
    return "default-key-change-in-production";
  })();

interface SecureStorage {
  setToken(token: string): void;
  getToken(): string | null;
  removeToken(): void;
  isTokenValid(): boolean;
}

class SecureTokenStorage implements SecureStorage {
  private encrypt(text: string): string {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
  }

  private decrypt(ciphertext: string): string {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  setToken(token: string): void {
    try {
      const encryptedToken = this.encrypt(token);
      const expiry = new Date().getTime() + 24 * 60 * 60 * 1000; // 24 hours
      const storage = {
        token: encryptedToken,
        expiry: expiry,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    } catch (error) {
      console.error("Failed to store token securely:", error);
    }
  }

  getToken(): string | null {
    try {
      const item = sessionStorage.getItem(STORAGE_KEY);
      if (!item) return null;

      const storage = JSON.parse(item);
      const now = new Date().getTime();

      // Check if token has expired
      if (now > storage.expiry) {
        this.removeToken();
        return null;
      }

      return this.decrypt(storage.token);
    } catch (error) {
      console.error("Failed to retrieve token:", error);
      this.removeToken();
      return null;
    }
  }

  removeToken(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  isTokenValid(): boolean {
    return this.getToken() !== null;
  }
}

export const tokenStorage = new SecureTokenStorage();

// Backward compatibility function for gradual migration
export const getAuthToken = (): string => {
  // First try new secure storage
  const secureToken = tokenStorage.getToken();
  if (secureToken) return secureToken;

  // Fallback to localStorage for existing tokens
  const legacyToken = localStorage.getItem("token");
  if (legacyToken) {
    // Migrate to secure storage
    tokenStorage.setToken(legacyToken);
    localStorage.removeItem("token");
    return legacyToken;
  }

  return "";
};
